import * as Dialog from '@radix-ui/react-dialog'
import { ExternalLink, ImageOff, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { companyFormResolver } from '../../lib/form-resolvers'
import { normalizeWebsiteUrl, parseEmailChips } from '../../lib/normalization'
import { CompanyFormFields } from '../forms/CompanyFormFields'
import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'

import type { Company } from '../../types/company'
import type { CompanyFormInput, EditCompanyMutationInput } from '../../types/forms'

interface EditCompanyDialogProps {
  company: Company | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (companyId: string, input: EditCompanyMutationInput) => Promise<void>
}

function valuesForCompany(company: Company): CompanyFormInput {
  return {
    name: company.name,
    businessActivity: company.business_activity,
    websiteUrl: company.website_url,
    emails: { items: company.emails.map(({ email }) => email), draft: '' },
    image: null,
  }
}

export function EditCompanyDialog({ company, open, onOpenChange, onSave }: EditCompanyDialogProps) {
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false)
  const form = useForm<CompanyFormInput>({
    resolver: companyFormResolver,
    defaultValues: company ? valuesForCompany(company) : undefined,
  })

  useEffect(() => {
    if (!company || !open) return
    form.reset(valuesForCompany(company))
    setRemoveCurrentImage(false)
  }, [company, form, open])

  if (!company) return null

  const submitting = form.formState.isSubmitting
  const selectedImage = form.watch('image')

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root')
    try {
      const website = normalizeWebsiteUrl(values.websiteUrl)
      const parsedEmails = parseEmailChips(values.emails)
      await onSave(company.id, {
        name: values.name,
        businessActivity: values.businessActivity,
        websiteUrl: website.websiteUrl,
        websiteNormalized: website.websiteNormalized,
        emails: parsedEmails.emails,
        image: values.image,
        removeCurrentImage: removeCurrentImage && !values.image,
      })
      onOpenChange(false)
    } catch (error) {
      form.setError('root.server', {
        message: error instanceof Error ? error.message : 'No fue posible guardar los cambios.',
      })
    }
  })

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => !submitting && onOpenChange(nextOpen)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/55 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in" />
        <Dialog.Content
          className="fixed inset-x-0 bottom-0 z-50 max-h-[94vh] overflow-y-auto rounded-t-3xl bg-paper shadow-2xl focus:outline-none sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:w-[min(42rem,calc(100%-2rem))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl"
          aria-describedby="edit-company-description"
          onEscapeKeyDown={(event) => submitting && event.preventDefault()}
        >
          <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-paper/95 px-5 py-5 backdrop-blur sm:px-7">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-moss">
                Editar registro
              </p>
              <Dialog.Title className="mt-1 font-display text-2xl font-bold tracking-tight">
                {company.name}
              </Dialog.Title>
              <Dialog.Description
                id="edit-company-description"
                className="mt-1 text-sm text-ink/60"
              >
                Actualiza datos, correos e imagen de la empresa.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Cerrar edición" disabled={submitting}>
                <X aria-hidden="true" className="h-5 w-5" />
              </Button>
            </Dialog.Close>
          </div>

          <form className="p-5 sm:p-7" onSubmit={(event) => void submit(event)} noValidate>
            <CompanyFormFields
              form={form}
              disabled={submitting}
              idPrefix="edit"
              currentImageUrl={removeCurrentImage ? null : company.image?.public_url}
              currentImageName={removeCurrentImage ? null : company.image?.original_name}
            />

            {company.image && !selectedImage && (
              <div className="mt-4 flex flex-col gap-3 rounded-xl border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold">Imagen actual</p>
                  <a
                    href={company.image.public_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-moss underline decoration-moss/30 underline-offset-4 hover:decoration-moss"
                  >
                    Ver imagen <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                  </a>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-red-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink/20 text-red-700 focus:ring-red-600"
                    checked={removeCurrentImage}
                    onChange={(event) => setRemoveCurrentImage(event.target.checked)}
                    disabled={submitting}
                  />
                  <ImageOff aria-hidden="true" className="h-4 w-4" />
                  Quitar imagen actual
                </label>
              </div>
            )}

            {form.formState.errors.root?.server?.message && (
              <div
                className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
                role="alert"
              >
                {form.formState.errors.root.server.message}
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Button variant="secondary" disabled={submitting}>
                  Cancelar
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Spinner /> : <Save aria-hidden="true" className="h-4 w-4" />}
                {submitting ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
